import React from 'react';
import { MoveRight, FileClock, Gauge } from 'lucide-react';

/**
 * Компонент раздела с показаниями счетчика
 */
const MeterReadingsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            name="hasPreviousReadings"
            checked={formData.hasPreviousReadings}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 flex items-center">
            <FileClock className="w-5 h-5 mr-1 text-blue-500" />
            Расчет по показаниям счетчика
          </span>
        </label>
      </div>

      {formData.hasPreviousReadings && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            {/* Предыдущие показания */}
            <div>
              <label htmlFor="previousReading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Предыдущие показания
              </label>
              <input
                type="text"
                id="previousReading"
                name="previousReading"
                value={formData.previousReading}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.previousReading ? 'input-error' : ''}`}
                placeholder="Например, 5750"
              />
              {errors.previousReading && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.previousReading}
                </p>
              )}
            </div>

            <div className="flex items-center justify-center text-blue-500 mt-6">
              <MoveRight className="w-5 h-5" />
            </div>

            {/* Текущие показания */}
            <div>
              <label htmlFor="currentReading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Текущие показания
              </label>
              <input
                type="text"
                id="currentReading"
                name="currentReading"
                value={formData.currentReading}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.currentReading ? 'input-error' : ''}`}
                placeholder="Например, 6000"
              />
              {errors.currentReading && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.currentReading}
                </p>
              )}
            </div>
          </div>

          {/* Расчет разницы показаний */}
          {!errors.previousReading && !errors.currentReading && formData.previousReading && formData.currentReading && (
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-blue-100 dark:border-blue-900/50 text-center">
              <p className="text-blue-700 dark:text-blue-300 font-medium">
                Потребление за период: <span className="text-lg">{(parseFloat(formData.currentReading) - parseFloat(formData.previousReading)).toFixed(0)}</span> кВт⋅ч
              </p>
            </div>
          )}

          {/* Информация о показаниях */}
          <div className="mt-4 bg-white dark:bg-gray-800 p-3 rounded-lg border border-blue-100 dark:border-blue-900/50">
            <div className="flex items-start gap-2">
              <Gauge className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Как правильно снимать показания счетчика?</h4>
                <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
                  <li>Для однотарифного счетчика запишите все цифры до запятой</li>
                  <li>Для многотарифного счетчика проверьте и запишите показания для каждого тарифа (T1, T2, T3)</li>
                  <li>Показания обычно снимаются в один и тот же день каждого месяца</li>
                  <li>Передавайте показания в период с 15 по 25 число месяца</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center mt-4 mb-3">
        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            name="includeExtraFees"
            checked={formData.includeExtraFees}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2">Учитывать дополнительные параметры</span>
        </label>
      </div>

      {formData.includeExtraFees && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Количество проживающих */}
            <div>
              <label htmlFor="householdMembers" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Количество проживающих
              </label>
              <input
                type="number"
                id="householdMembers"
                name="householdMembers"
                value={formData.householdMembers}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.householdMembers ? 'input-error' : ''}`}
                min="1"
                step="1"
              />
              {errors.householdMembers && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.householdMembers}
                </p>
              )}
            </div>

            {/* Площадь жилья */}
            <div>
              <label htmlFor="householdArea" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Площадь жилья (м²)
              </label>
              <input
                type="number"
                id="householdArea"
                name="householdArea"
                value={formData.householdArea}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.householdArea ? 'input-error' : ''}`}
                min="1"
                step="0.1"
              />
              {errors.householdArea && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.householdArea}
                </p>
              )}
            </div>

            {/* Социальная норма */}
            <div>
              <label htmlFor="socialNorm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Социальная норма (кВт⋅ч)
              </label>
              <input
                type="number"
                id="socialNorm"
                name="socialNorm"
                value={formData.socialNorm}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.socialNorm ? 'input-error' : ''}`}
                min="0"
                step="10"
              />
              {errors.socialNorm && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.socialNorm}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Норма для базового тарифа (зависит от региона и количества проживающих)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeterReadingsSection;